import {
  ShortAlertResponse,
  ShortAlertResponseFast,
  ValidationAlertSummary,
} from 'app/modules/alerts/types/responses';
import { InvestigationTableColumn } from 'app/modules/investigations/models';

import { INVESTIGATION_COLUMN_CONFIG } from 'app/modules/investigations/columns';
import { ROUTES_MAP } from 'app/shared/utils/routes';
import { ALERT_TYPE_LABELS } from 'app/modules/alerts/constants';

import { AlertAmount } from 'app/modules/alerts/components/AlertAmount';
import { AiAgentRecommendationChip } from 'app/modules/aiAgentResults/components/AiAgentRecommendationChip';
import {
  IconArrowRight,
  IconClipboardList,
  IconCreditCard,
  IconFlag,
  IconSparkles,
  IconScale,
} from '@u21/tabler-icons';
import {
  U21TableBodyCellProps,
  U21TableColumn,
  U21Typography,
} from 'app/shared/u21-ui/components';
import { DeadlineChip } from 'app/modules/deadlines/components/DeadlineChip';
import { ArticleDeadline } from 'app/modules/deadlines/models';
import { AIRecommendation } from 'app/modules/aiAgentResults/models';

export type AlertTableType = ShortAlertResponse | ShortAlertResponseFast;
export type AlertTableColumn = U21TableColumn<AlertTableType, any>;
export type ValidationAlertTableColumn = U21TableColumn<
  ValidationAlertSummary,
  any
>;

const AMOUNT_COLUMN: AlertTableColumn = {
  id: 'amount_volume',
  Cell: (props: U21TableBodyCellProps<AlertTableType>) => {
    const { row } = props;
    return <AlertAmount alert={row} />;
  },
  headerIcon: <IconCreditCard />,
};

const SHARED_GOTO_COLUMN = {
  id: 'goto',
  type: 'button',
  cellProps: (alert: { id: number }) => ({
    children: <IconArrowRight />,
    to: ROUTES_MAP.alertsId.path.replace(':id', String(alert.id)),
  }),
  disableSortBy: true,
} as const;

export const GOTO_COLUMN: AlertTableColumn = SHARED_GOTO_COLUMN;
export const VALIDATION_GOTO_COLUMN: ValidationAlertTableColumn =
  SHARED_GOTO_COLUMN;

const SHARED_CONDITION_BREAKDOWN_COLUMN = {
  id: 'condition_breakdown',
  type: 'button',
} as const;

export const VALIDATION_BREAKDOWN_COLUMN: ValidationAlertTableColumn =
  SHARED_CONDITION_BREAKDOWN_COLUMN;

export const RULES_BREAKDOWN_COLUMN: AlertTableColumn =
  SHARED_CONDITION_BREAKDOWN_COLUMN;

const QUEUE_COLUMN: AlertTableColumn = {
  id: 'queue.title',
  accessor: (alert) => alert['queue.title'],
  headerIcon: <IconClipboardList />,
};

const QUEUE_TEAMS_COLUMN: AlertTableColumn = {
  id: 'queue.teams',
  accessor: (alert) => (alert['queue.teams'] || []).map((i) => i.name),
  type: 'list',
};

const RULES_COLUMN: AlertTableColumn = {
  id: 'rules',
  accessor: (row) => row.rules.map((i) => i.title),
  cellProps: {
    getShowLessText: () => 'Show fewer rules',
    getShowMoreText: (length) => `Show all ${length} rules`,
  },
  headerIcon: <IconScale />,
  type: 'list',
  disableSortBy: true,
};

export const TITLE_COLUMN: InvestigationTableColumn = {
  id: 'title',
  cellProps: { variant: 'subtitle2' },
  headerIcon: <IconFlag />,
  width: 300,
};

const TYPE_COLUMN: AlertTableColumn = {
  id: 'alert_type',
  accessor: (row) => ALERT_TYPE_LABELS[row.alert_type],
};

const DEADLINE_COLUMN: AlertTableColumn = {
  id: 'deadline',
  type: 'label',
  Cell: ({
    value,
    row,
  }: U21TableBodyCellProps<AlertTableType, ArticleDeadline>) => (
    <DeadlineChip
      articleDeadline={value}
      deadlinesConfig={row.deadline_config}
      closed={row.status !== 'OPEN'}
    />
  ),
};

export const AI_RECOMMENDATION_COLUMN: AlertTableColumn = {
  id: 'ai_agent_recommendation',
  Header: () => (
    <U21Typography
      icon={<IconSparkles />}
      color="text.secondary"
      variant="subtitle2"
    >
      AI recommendation
    </U21Typography>
  ),
  Cell: ({
    value,
    row,
  }: U21TableBodyCellProps<AlertTableType, AIRecommendation>) => {
    return (
      <AiAgentRecommendationChip
        clickType="ALERT"
        id={String(row.id)}
        value={value}
        type="ALERT"
      />
    );
  },
  width: 240,
};

export const ACTIONS_COLUMN: U21TableColumn<any> = {
  id: 'actions',
  type: 'actions',
};

// legacy column ID that we need to hide on the UI
export const ALERT_SCORE_COLUMN_ID = 'alert_score';

export const ALERT_COLUMN_CONFIG = {
  ...INVESTIGATION_COLUMN_CONFIG,
  [AI_RECOMMENDATION_COLUMN.id]: AI_RECOMMENDATION_COLUMN,
  [AMOUNT_COLUMN.id]: AMOUNT_COLUMN,
  [QUEUE_COLUMN.id]: QUEUE_COLUMN,
  [QUEUE_TEAMS_COLUMN.id]: QUEUE_TEAMS_COLUMN,
  [RULES_COLUMN.id]: RULES_COLUMN,
  [TITLE_COLUMN.id]: TITLE_COLUMN,
  [TYPE_COLUMN.id]: TYPE_COLUMN,
  [DEADLINE_COLUMN.id]: DEADLINE_COLUMN,
} as Record<string, AlertTableColumn>; // cast to make typing more specific to alerts
